import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Briefcase } from 'lucide-react';

const PostRole = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState({
    employer_id: user.id,
    title: '',
    location: '',
    trade_category: '',
    intake_size: 1,
    min_readiness_score: 70,
    min_aptitude_score: 65,
    required_interests: []
  });
  const [posting, setPosting] = useState(false);

  const allInterests = ['Technology', 'Building', 'Problem Solving', 'Teamwork', 'Design', 'Hands-on Work', 'Customer Service', 'Innovation'];
  const trades = ['IT Support', 'Construction', 'Electrical', 'Plumbing', 'Manufacturing', 'Automotive', 'Carpentry', 'Hospitality'];
  const locations = ['Dublin', 'Cork', 'Galway', 'Limerick', 'Waterford', 'Kilkenny'];

  const toggleInterest = (interest) => {
    setRole(prev => ({
      ...prev,
      required_interests: prev.required_interests.includes(interest)
        ? prev.required_interests.filter(i => i !== interest)
        : [...prev.required_interests, interest]
    }));
  };

  const handleSubmit = async () => {
    if (!role.title || !role.location || !role.trade_category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setPosting(true);
    try {
      await axios.post(`${API_BASE}/roles`, role);
      toast.success('Role posted successfully! Matches are being calculated.');
      setTimeout(() => navigate('/employer/candidates'), 1500);
    } catch (error) {
      toast.error('Failed to post role');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Post a New Role</h1>
        <p className="text-lg text-slate-600 mb-8">Define your requirements to find the perfect apprentice</p>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-6">
          <div>
            <Label htmlFor="title">Role Title *</Label>
            <Input
              id="title"
              value={role.title}
              onChange={(e) => setRole({ ...role, title: e.target.value })}
              placeholder="e.g., Electrician"
              className="mt-1"
              data-testid="role-title-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">Location *</Label>
              <select
                id="location"
                value={role.location}
                onChange={(e) => setRole({ ...role, location: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
                data-testid="role-location-select"
              >
                <option value="">Select location</option>
                {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="trade_category">Trade Category *</Label>
              <select
                id="trade_category"
                value={role.trade_category}
                onChange={(e) => setRole({ ...role, trade_category: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
                data-testid="role-trade-select"
              >
                <option value="">Select trade</option>
                {trades.map(trade => <option key={trade} value={trade}>{trade}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="intake_size">Intake Size</Label>
            <Input
              id="intake_size"
              type="number"
              min="1"
              value={role.intake_size}
              onChange={(e) => setRole({ ...role, intake_size: parseInt(e.target.value) })}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="min_readiness">Minimum Readiness Score: {role.min_readiness_score}</Label>
            <input
              id="min_readiness"
              type="range"
              min="0"
              max="100"
              value={role.min_readiness_score}
              onChange={(e) => setRole({ ...role, min_readiness_score: parseInt(e.target.value) })}
              className="w-full mt-2"
              data-testid="readiness-slider"
            />
          </div>

          <div>
            <Label htmlFor="min_aptitude">Minimum Aptitude Score: {role.min_aptitude_score}</Label>
            <input
              id="min_aptitude"
              type="range"
              min="0"
              max="100"
              value={role.min_aptitude_score}
              onChange={(e) => setRole({ ...role, min_aptitude_score: parseInt(e.target.value) })}
              className="w-full mt-2"
              data-testid="aptitude-slider"
            />
          </div>

          <div>
            <Label>Required Interests</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allInterests.map(interest => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-4 py-2 rounded-full border transition-all ${
                    role.required_interests.includes(interest)
                      ? 'bg-[#0D9488] text-white border-[#0D9488]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]'
                  }`}
                  data-testid={`interest-${interest.toLowerCase().replace(' ', '-')}`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={posting}
            className="w-full bg-[#0D9488] hover:bg-[#115E59] rounded-full"
            data-testid="post-role-btn"
          >
            <Briefcase className="mr-2 w-4 h-4" />
            {posting ? 'Posting...' : 'Post Role'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostRole;
